import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Typography } from 'antd';
import { useAuth } from '../context/AuthContext';
import { useSoftphone } from '../context/SoftphoneContext';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

// Configuration
const INACTIVITY_TIMEOUT = 120 * 60 * 1000; // 120 minutes (2 hours)
const WARNING_TIME = 60 * 1000; // 1 minute warning
const CHECK_INTERVAL = 1000; // Check every second

const InactivityMonitor = () => {
    const { logout, refreshSession, isAuthenticated } = useAuth();
    const { inCall, isCalling, isRinging } = useSoftphone();
    const navigate = useNavigate();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [timeLeft, setTimeLeft] = useState(Math.floor(WARNING_TIME / 1000));
    const lastActivityRef = useRef(Date.now());
    const intervalRef = useRef(null);

    const isInProgressCall = inCall || isCalling || isRinging;

    const handleActivity = useCallback(() => {
        lastActivityRef.current = Date.now();
    }, []);

    const handleLogout = useCallback(() => {
        setIsModalVisible(false);
        logout(true);
        navigate('/login?expired=true');
    }, [logout, navigate]);

    const checkForInactivity = useCallback(() => {
        if (!isAuthenticated) return;

        const now = Date.now();
        const inactiveTime = now - lastActivityRef.current;
        const timeRemaining = INACTIVITY_TIMEOUT - inactiveTime;

        if (inactiveTime >= INACTIVITY_TIMEOUT) {
            handleLogout();
        } else if (timeRemaining <= WARNING_TIME) {
            if (!isModalVisible) {
                setIsModalVisible(true);
            }
            setTimeLeft(Math.max(0, Math.floor(timeRemaining / 1000)));
        } else if (timeRemaining > WARNING_TIME && isModalVisible) {
            setIsModalVisible(false);
        }
    }, [isAuthenticated, isModalVisible, handleLogout]);

    const handleStayConnected = async () => {
        const success = await refreshSession();
        if (success) {
            lastActivityRef.current = Date.now();
            setIsModalVisible(false);
        } else {
            // If refresh fails, it might have been caught by the interceptor already
            // but just in case, we log out.
            handleLogout();
        }
    };

    // Reset activity if a call is in progress
    useEffect(() => {
        if (isInProgressCall) {
            lastActivityRef.current = Date.now();
            if (isModalVisible) {
                setIsModalVisible(false);
            }
        }
    }, [isInProgressCall, isModalVisible]);

    useEffect(() => {
        if (isAuthenticated) {
            lastActivityRef.current = Date.now();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

        // Throttled activity handler could be better but for simple state update Ref is fine
        const activityHandler = () => {
            // Only update if not in warning state to avoid auto-closing or confusing logic
            // actually we should update always to prevent showing it if user is working
            // BUT requirements say: "Si el usuario no interactúa con el modal..."
            // So if the modal IS visible, we might NOT want to auto-dismiss just by mouse move, 
            // strictly following "Botón B: Mantenerse conectado". 
            // However, typical behavior is any activity resets. 
            // The requirements say: "Acción 'Mantenerse conectado': ... ejecutará /session_ping".
            // It implies explicit action. 
            // Let's safe guard: If modal is Open, ignore passive activity? 
            // "Si el usuario no interactúa con el modal... Cerrar automáticamente... Redirigir"
            // This strongly implies we wait for specific interaction with the MODAL.

            if (!isModalVisible) {
                handleActivity();
            }
        };

        events.forEach(event => window.addEventListener(event, activityHandler));

        intervalRef.current = setInterval(checkForInactivity, CHECK_INTERVAL);

        return () => {
            events.forEach(event => window.removeEventListener(event, activityHandler));
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isAuthenticated, isModalVisible, handleActivity, checkForInactivity]);

    if (!isModalVisible) return null;

    return (
        <Modal
            title="¿Sigues allí?"
            open={isModalVisible}
            footer={null}
            closable={false}
            maskClosable={false}
            centered
        >
            <div style={{ textAlign: 'center' }}>
                <p style={{ marginBottom: 10 }}>
                    Tu sesión está a punto de expirar por inactividad en:
                </p>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#DA291C', marginBottom: 20 }}>
                    {timeLeft} segundos
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                    <Button onClick={handleLogout} danger>
                        Cerrar sesión
                    </Button>
                    <Button type="primary" onClick={handleStayConnected} style={{ background: '#DA291C', borderColor: '#DA291C' }}>
                        Mantenerse conectado
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default InactivityMonitor;
